/**
 * <ec-header> — desktop dropdowns (click/keyboard, hover is CSS), full width
 * mega panels ([data-ec-mega-item]: hover with a 150ms close delay, keyboard
 * focus, click; state is the toggle's aria-expanded), mobile
 * drawer with sliding sub-menu panels and a focus trap, and a shadow once a
 * sticky header has scrolled.
 *
 * Drawer panels: [data-ec-panel] elements; the main one has .is-active.
 * [data-ec-panel-open="id"] slides panel `id` in, [data-ec-panel-back] returns
 * to the panel named in its panel's data-ec-parent. Hidden panels are `inert`.
 */
if (!customElements.get('ec-header')) {
  class EcHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-ec-drawer]');
      this.panel = this.drawer?.querySelector('[role="dialog"]');
      this.openButton = this.querySelector('[data-ec-drawer-open]');
      this.toggles = Array.from(this.querySelectorAll('[data-ec-dropdown-toggle]'));
      this.levels = Array.from(this.drawer?.querySelectorAll('[data-ec-panel]') || []);
      this.mainLevel = this.levels[0];
      this.activeLevel = this.mainLevel;

      // the menu button doubles as the close button while the drawer is open
      this.openButton?.addEventListener('click', () => {
        if (this.openButton.getAttribute('aria-expanded') === 'true') this.closeDrawer();
        else this.openDrawer();
      });
      this.querySelectorAll('[data-ec-drawer-close]').forEach((el) =>
        el.addEventListener('click', () => this.closeDrawer())
      );
      this.drawer?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeDrawer();
        if (event.key === 'Tab') this.trapFocus(event);
      });

      this.drawer?.querySelectorAll('[data-ec-panel-open]').forEach((button) => {
        button.addEventListener('click', () => this.showLevel(button.dataset.ecPanelOpen, button));
      });
      this.drawer?.querySelectorAll('[data-ec-panel-back]').forEach((button) => {
        button.addEventListener('click', () => this.backLevel());
      });

      this.onResize = () => {
        if (this.drawer && !this.drawer.hidden) this.positionDrawer();
      };
      window.addEventListener('resize', this.onResize);

      this.toggles.forEach((toggle) => {
        const item = toggle.closest('.ec-header__menu-item');
        const isMega = item.hasAttribute('data-ec-mega-item');
        toggle.addEventListener('click', (event) => {
          // a mouse click on a panel that hover already opened keeps it open
          if (isMega && event.detail > 0 && toggle.getAttribute('aria-expanded') === 'true') return;
          this.toggleDropdown(toggle);
        });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.setOpen(toggle, false);
        });
        if (!isMega) {
          item.addEventListener('mouseleave', () => this.setOpen(toggle, false));
          return;
        }
        // Mega panels: open on hover / keyboard focus, close 150ms after the
        // mouse leaves so crossing a gap does not close them
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.megaCloseTimer);
          this.openOnly(toggle);
        });
        item.addEventListener('mouseleave', () => {
          clearTimeout(this.megaCloseTimer);
          this.megaCloseTimer = setTimeout(() => this.setOpen(toggle, false), 150);
        });
        item.addEventListener('focusin', (event) => {
          // the toggle itself stays a plain open/close button
          if (this.skipFocusOpen || event.target === toggle) return;
          if (event.target.matches(':focus-visible')) this.openOnly(toggle);
        });
      });

      // mega column accordions: only the chevron toggles, one open item per column
      this.addEventListener('click', (event) => {
        const chevron = event.target.closest('[data-ec-mega-accordion]');
        if (chevron) this.toggleAccordion(chevron);
      });

      // hovering any other top item closes an open mega panel right away
      this.querySelectorAll('.ec-header__menu-item:not([data-ec-mega-item])').forEach((item) => {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.megaCloseTimer);
          this.toggles.forEach((toggle) => {
            if (!item.contains(toggle)) this.setOpen(toggle, false);
          });
        });
      });

      // full width panels: page width without the scrollbar
      this.setPageWidth = () =>
        this.style.setProperty('--ec-page-width', `${document.documentElement.clientWidth}px`);
      this.setPageWidth();
      window.addEventListener('resize', this.setPageWidth);

      // theme editor: show a Mega menu block's panel while it is selected
      this.onBlockSelect = (event) => {
        const panel = event.target.closest?.('.ec-mega');
        if (!panel || !this.contains(panel)) return;
        const toggle = this.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`);
        if (toggle) this.openOnly(toggle);
      };
      this.onBlockDeselect = (event) => {
        if (this.contains(event.target)) this.closeDropdowns();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeDropdowns();
      };
      this.onDocumentKeydown = (event) => {
        if (event.key !== 'Escape') return;
        const open = this.toggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true');
        if (!open) return;
        const hadFocus = open.closest('.ec-header__menu-item').contains(document.activeElement);
        this.closeDropdowns();
        if (!hadFocus) return;
        // returning focus to the toggle must not reopen its mega panel
        this.skipFocusOpen = true;
        open.focus();
        this.skipFocusOpen = false;
      };
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);

      // a menu link to the same page (#hash only) does not reload: close menus
      this.onHashChange = () => {
        this.closeDropdowns();
        this.closeDrawer(false);
      };
      window.addEventListener('hashchange', this.onHashChange);

      if (this.classList.contains('ec-header--sticky')) {
        this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 4);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      this.desktopQuery = window.matchMedia('(min-width: 1024px)');
      this.onBreakpoint = (event) => {
        if (event.matches) this.closeDrawer(false);
      };
      this.desktopQuery.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('resize', this.setPageWidth);
      window.removeEventListener('hashchange', this.onHashChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      clearTimeout(this.megaCloseTimer);
      this.desktopQuery?.removeEventListener('change', this.onBreakpoint);
      document.body.classList.remove('ec-drawer-lock');
    }

    toggleDropdown(toggle) {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      this.openOnly(isOpen ? null : toggle);
    }

    // open `toggle`'s dropdown / panel and close every other one
    openOnly(toggle) {
      this.toggles.forEach((other) => this.setOpen(other, other === toggle));
    }

    closeDropdowns() {
      this.openOnly(null);
    }

    setOpen(toggle, open) {
      if ((toggle.getAttribute('aria-expanded') === 'true') === open) return;
      toggle.setAttribute('aria-expanded', String(open));
      const item = toggle.closest('.ec-header__menu-item');
      item.classList.toggle('is-open', open);
      if (!item.hasAttribute('data-ec-mega-item')) return;
      if (!open) {
        // a closed panel starts with every accordion closed next time
        item.querySelectorAll('[data-ec-mega-accordion]').forEach((chevron) => this.setAccordion(chevron, false));
        return;
      }
      // a tall panel scrolls inside the viewport instead of past it
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      const top = this.getBoundingClientRect().bottom;
      panel?.style.setProperty('--ec-mega-max-height', `${Math.max(240, window.innerHeight - top)}px`);
    }

    toggleAccordion(chevron) {
      const open = chevron.getAttribute('aria-expanded') !== 'true';
      if (open) {
        chevron
          .closest('.ec-mega__column')
          .querySelectorAll('[data-ec-mega-accordion][aria-expanded="true"]')
          .forEach((other) => this.setAccordion(other, false));
      }
      this.setAccordion(chevron, open);
    }

    setAccordion(chevron, open) {
      chevron.setAttribute('aria-expanded', String(open));
      chevron.closest('.ec-mega__item').classList.toggle('is-expanded', open);
    }

    // The drawer starts right below the header so the header (and its close icon) stays visible
    positionDrawer() {
      const top = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
      this.drawer.style.setProperty('--ec-drawer-top', `${top}px`);
    }

    openDrawer() {
      if (!this.drawer) return;
      clearTimeout(this.hideTimer);
      this.resetLevels();
      this.positionDrawer();
      this.drawer.hidden = false;
      // next frame so the slide-in transition runs
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      });
      this.openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ec-drawer-lock');
      this.panel?.focus();
    }

    closeDrawer(returnFocus = true) {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      this.openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ec-drawer-lock');
      this.hideTimer = setTimeout(() => {
        this.drawer.hidden = true;
        this.resetLevels();
      }, 350);
      if (returnFocus) this.openButton?.focus();
    }

    // --- Sub-menu panels ----------------------------------------------------

    setLevelState(level, state) {
      level.classList.toggle('is-active', state === 'active');
      level.classList.toggle('is-behind', state === 'behind');
      level.inert = state !== 'active';
    }

    resetLevels() {
      this.levels.forEach((level) => {
        this.setLevelState(level, level === this.mainLevel ? 'active' : 'hidden');
        level.scrollTop = 0;
      });
      this.drawer?.querySelectorAll('[data-ec-panel-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.activeLevel = this.mainLevel;
    }

    showLevel(id, opener) {
      const target = this.drawer.querySelector(`#${CSS.escape(id)}`);
      if (!target || target === this.activeLevel) return;
      this.setLevelState(this.activeLevel, 'behind');
      target.scrollTop = 0;
      this.setLevelState(target, 'active');
      opener?.setAttribute('aria-expanded', 'true');
      this.activeLevel = target;
      target.querySelector('[data-ec-panel-back]')?.focus({ preventScroll: true });
    }

    backLevel() {
      const current = this.activeLevel;
      const parent = this.drawer.querySelector(`#${CSS.escape(current.dataset.ecParent || '')}`);
      if (!parent) return;
      this.setLevelState(current, 'hidden');
      this.setLevelState(parent, 'active');
      this.activeLevel = parent;
      const opener = parent.querySelector(`[data-ec-panel-open="${CSS.escape(current.id)}"]`);
      opener?.setAttribute('aria-expanded', 'false');
      opener?.focus({ preventScroll: true });
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.panel.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.closest('[inert]') && el.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('ec-header', EcHeader);
}
